import { Injectable } from '@angular/core';
import { ApiHttpClient, ListOption, ListOptionRequest } from '../modules/tcm-core.module';
import { EarSideExtendedEnum, ProductBundleTypeEnum, ProductKitTypeEnum } from '../models/enums';
import { ProductCategoryListOptionRequest, ProductCategoryOptionWithGroup, ProductCategoryRankEnum } from '../models/product-categories';
import { ProductAddMultipleViewModel, ProductOptionRequest, ProductStyleFkInfoViewModel, ProductStyleFullDetailsViewModel, ProductStyleInfoViewModel, ProductStyleOptionForInsuranceBenefitRequest, ProductStyleOptionForSalesOrderRequest, ProductStyleOptionForSalesOrderWithProductStyleInfoRequest, ProductStyleOptionRequest, ProductStyleOptionWithDetails, ProductStyleOptionWithDetailsRequest, ProductStyleOptionWithProductStyleInfo, ProductInfoRequest, DispensingProductStyleOptionRequest, ProductKitModel, ProductKitViewModel, ProductBundleViewModel } from '../models/products';
import { ProductCopyRequest } from '../models/products/product-copy-request';
import { DeletProductStyleColor, ProductStyleColorViewModel } from '../models/products/product-styles/product-style-color-linked-search-request';
import { ProductStyleColorsOptionRequest } from '../models/products/product-styles/product-style-colors-option-request';
import { RepairTypeEnum } from '../models/products/repair-type-enum';

@Injectable({
  providedIn: 'root'
})
export class ProductDataService {

  constructor(private readonly httpClient: ApiHttpClient) {
  }

  getProductsOptionsAsync(query: string, timeout: Promise<any> = null): Promise<ListOption<number>[]> {
    const request: ProductOptionRequest = { searchString: query };
    return this.httpClient.getJsonAsync<ListOption<number>[]>('Products/Options', request, timeout);
  }

  // For Maintaining the state of selectedRows
  selectedRows: any[];

  //For Tracking All Select Checkbox
  isToggle: boolean;
  isAllSelected: boolean;

  manufacturerColorOptions: ListOption<number>[];

  getProductStyleInfoAsync(productStyleId: number, salesOrderId: number, salesOrderLineId: number = null, isBinaural: boolean = false, serviceCodeId: number = null,
    isProductStyleIdChanged: boolean = false, currentVirtualProducts: ProductInfoRequest[] = [], isKitItem: boolean = false, earSide: EarSideExtendedEnum = null, timeout: Promise<any> = null): Promise<ProductStyleInfoViewModel> {
    const request = {
      productStyleId: productStyleId,
      salesOrderId: salesOrderId,
      salesOrderLineId: salesOrderLineId,
      isBinaural: (isBinaural || false),
      serviceCodeId: serviceCodeId,
      isProductStyleIdChanged: isProductStyleIdChanged,
      currentVirtualProducts: currentVirtualProducts,
      isKitItem: isKitItem,
      earSide: earSide
    };
    return this.httpClient.postJsonAsync<ProductStyleInfoViewModel>('ProductStyles/ProductStyleInfo', request, timeout);
  }

  getProductStyleFkInfoAsync(productStyleId: number, timeout: Promise<any> = null): Promise<ProductStyleFkInfoViewModel> {
    const request = { productStyleId: productStyleId };
    return this.httpClient.getJsonAsync<ProductStyleFkInfoViewModel>('ProductStyles/ProductStyleFkInfo', request, timeout);
  }

  getProductStyleFullDetailsAsync(productStyleId: number, timeout: Promise<any> = null): Promise<ProductStyleFullDetailsViewModel> {
    const request = { productStyleId: productStyleId };
    return this.httpClient.getJsonAsync<ProductStyleFullDetailsViewModel>('ProductStyles/ProductStyleFullDetails', request, timeout);
  }

  addMultipleProductStylesAsync(request: ProductAddMultipleViewModel, cancellation: Promise<any>): Promise<any> {
    return this.httpClient.postJsonAsync('ProductStyles/AddMultipleProductStyles', request, cancellation);
  }

  getProductStylesOptionsAsync(query: string,
    productGroupIds: number[] = null,
    productCategoryId: number = null,
    isSerialized: boolean = null,
    salesOrderId: number = null,
    cancellationToken: Promise<any> = null): Promise<ListOption<number>[]> {
    const request: ProductStyleOptionRequest = { searchString: query, productGroupIds: productGroupIds, productCategoryId: productCategoryId, isSerialized: isSerialized, salesOrderId: salesOrderId };
    return this.httpClient.getJsonAsync<ListOption<number>[]>('ProductStyles/Options', request, cancellationToken);
  }

  getProductStylesOptionsFullAsync(query: string,
    productGroupIds: number[] = null,
    productCategoryId: number = null,
    productCategory2Id: number = null,
    isSerialized: boolean = null,
    cancellationToken: Promise<any> = null): Promise<ListOption<number>[]> {
    const request: ProductStyleOptionRequest = { searchString: query, productGroupIds: productGroupIds, productCategoryId: productCategoryId, productCategory2Id: productCategory2Id, isSerialized: isSerialized };
    return this.httpClient.getJsonAsync<ListOption<number>[]>('ProductStyles/Options', request, cancellationToken);
  }

  getProductStylesOptionsWithAllFiltersAsync(query: string,
    productGroupId: number = null,
    productCategoryId: number = null,
    productCategory2Id: number = null,
    manufacturerId: number = null,
    isSerialized: boolean = null,
    productStyleId: number = null,
    cancellationToken: Promise<any> = null): Promise<ListOption<number>[]> {
    const request: ProductStyleOptionRequest = { searchString: query, productGroupIds: (productGroupId ? [productGroupId] : null), productCategoryId: productCategoryId, productCategory2Id: productCategory2Id, isSerialized: isSerialized, manufacturerId: manufacturerId, productStyleId: productStyleId };
    return this.httpClient.getJsonAsync<ListOption<number>[]>('ProductStyles/Options', request, cancellationToken);
  }


  getProductStyleOptionsForSalesOrderAsync(query: string, salesOrderId: number, productCategoryId: number = null, productGroupIds: number[] = null, manufacturerId: number = null, timeout: Promise<any> = null): Promise<ProductStyleOptionWithDetails[]> {
    const request: ProductStyleOptionForSalesOrderRequest = { searchString: query, salesOrderId: salesOrderId, productCategoryId: productCategoryId, productGroupIds: productGroupIds, manufacturerId: manufacturerId };
    return this.httpClient.getJsonAsync<ProductStyleOptionWithDetails[]>('ProductStyles/OptionsForSalesOrder', request, timeout);
  }

  getProductStylesOptionsForInsuranceBenefitAsync(query: string, productGroupId: number = null, productCategory1Id: number = null, productCategory2Id: number = null, productLevelId: number = null, manufacturerId: number = null, batterySizeId: number = null, productStyleIdToInclude: number = null, timeout: Promise<any> = null): Promise<ListOption<number>[]> {
    const request: ProductStyleOptionForInsuranceBenefitRequest = { searchString: query, productGroupId: productGroupId, productCategory1Id: productCategory1Id, productCategory2Id: productCategory2Id, productLevelId: productLevelId, manufacturerId: manufacturerId, batterySizeId: batterySizeId, productStyleIdToInclude: productStyleIdToInclude };
    return this.httpClient.getJsonAsync<ListOption<number>[]>('ProductStyles/OptionsForInsuranceBenefit', request, timeout);
  }

  getDispensingProductStyleOptionsAsync(query: string, productStyleIdToInclude: number = null, timeout: Promise<any> = null): Promise<ListOption<number>[]> {
    const request: DispensingProductStyleOptionRequest = { searchString: query, productStyleIdToInclude };
    return this.httpClient.getJsonAsync<ListOption<number>[]>('ProductStyles/DispensingProductStyleOptions', request, timeout);
  }

  getProductStylesOptionsWithDetailsAsync(query: string, productCategoryId: number = null, productGroupIds: number[] = null, manufacturerId: number = null, timeout: Promise<any> = null): Promise<ProductStyleOptionWithDetails[]> {
    const request: ProductStyleOptionWithDetailsRequest = { searchString: query, productCategoryId: productCategoryId, productGroupIds: productGroupIds, manufacturerId: manufacturerId };
    return this.httpClient.getJsonAsync<ProductStyleOptionWithDetails[]>('ProductStyles/OptionsWithDetails', request, timeout);
  }

  getProductStyleOptionsForSalesOrderWithProductStyleInfoAsync(query: string, salesOrderId: number, timeout: Promise<any> = null): Promise<ProductStyleOptionWithProductStyleInfo[]> {
    const request: ProductStyleOptionForSalesOrderWithProductStyleInfoRequest = { searchString: query, salesOrderId: salesOrderId };
    return this.httpClient.getJsonAsync<ProductStyleOptionWithProductStyleInfo[]>('ProductStyles/OptionsForSalesOrderWithProductStyleInfo', request, timeout);
  }

  getProductGroupsOptionsAsync(query: string, timeout: Promise<any> = null): Promise<ListOption<number>[]> {
    const request: ListOptionRequest = { searchString: query };
    return this.httpClient.getJsonAsync<ListOption<number>[]>('ProductGroups/Options', request, timeout);
  }

  getProductCategoriesOptionsAsync(query: string, productGroupId: number = null, rank: ProductCategoryRankEnum = null, timeout: Promise<any> = null): Promise<ListOption<number>[]> {
    const request: ProductCategoryListOptionRequest = { searchString: query, productGroupId: productGroupId, rank: rank };
    return this.httpClient.getJsonAsync<ListOption<number>[]>('ProductCategories/Options', request, timeout);
  }

  getProductCategoriesOptionsWithGroupAsync(query: string = null, productGroupId: number = null, timeout: Promise<any> = null): Promise<ProductCategoryOptionWithGroup[]> {
    const request: ProductCategoryListOptionRequest = { searchString: query, productGroupId: productGroupId };
    return this.httpClient.getJsonAsync<ProductCategoryOptionWithGroup[]>('ProductCategories/OptionsWithGroup', request, timeout);
  }

  isProductCategoryInUseAsync(productCategoryId: number = null, timeout: Promise<any> = null): Promise<boolean> {
    return this.httpClient.getJsonAsync<boolean>('ProductCategories/IsInUse', { productCategoryId: productCategoryId }, timeout);
  }

  checkProductStyleExistsAsync(productId: number, name: string, id: number, timeout: Promise<any>): Promise<boolean> {
    const data = { name: name, productId: productId, id: id };
    return this.httpClient.getJsonAsync<boolean>('ProductStyles/CheckProductStyleExists', data, timeout, false);
  }

  getRepairProductStylesAsync(name: string, repairLocation: number, clinicId: number, insuranceId: number, manufacturerIds: number[], cancellation: Promise<any>): Promise<ProductStyleOptionWithProductStyleInfo[]> {
    return this.httpClient.postJsonAsync<ProductStyleOptionWithProductStyleInfo[]>('ProductStyles/GetRepairProductStyles', { searchString: name,repairLocation,  clinicId, insuranceId, manufacturerIds }, cancellation, false);
  }

  getProductStylesForProductKitAsync(name: string, productId: number, cancellation: Promise<any>): Promise<ListOption<number>[]> {
    return this.httpClient.getJsonAsync<ListOption<number>[]>('ProductStyles/GetProductStylesForProductKit', { searchString: name, productId }, cancellation, false);
  }

  getProductStylesForProductBundleAsync(name: string, productId: number, cancellation: Promise<any>): Promise<ListOption<number>[]> {
    return this.httpClient.getJsonAsync<ListOption<number>[]>('ProductStyles/GetProductStylesForProductBundle', { searchString: name, productId }, cancellation, false);
  }

  getLossAndDamageProductStylesAsync(name: string, clinicId: number, insuranceId: number, cancellation: Promise<any>): Promise<ListOption<number>[]> {
    return this.httpClient.getJsonAsync<ListOption<number>[]>('ProductStyles/GetLossAndDamageProductStyles', { searchString: name, clinicId, insuranceId }, cancellation, false);
  }

  getTrackableProductStylesOptionsForVendorAsync(name: string, vendorId: number, selectedProductStyleId: number, cancellation: Promise<any>): Promise<ListOption<number>[]> {
    return this.httpClient.getJsonAsync<ListOption<number>[]>('ProductStyles/GetTrackableProductStylesOptionsForVendor', { searchString: name, vendorId: vendorId, selectedProductStyleId: selectedProductStyleId }, cancellation, false);
  }

  getProductStyleUnitCost(vendorId: number, selectedProductStyleId: number, cancellation: Promise<any>): Promise<any> {
    return this.httpClient.getJsonAsync('ProductStyles/GetProductStyleUnitCost', { vendorId: vendorId, selectedProductStyleId: selectedProductStyleId }, cancellation, false);
  }

  getProductStyleColors(vendorId: number, selectedProductStyleId: number, cancellation: Promise<any>): Promise<any> {
    return this.httpClient.getJsonAsync('ProductStyles/GetProductStyleColors', { vendorId: vendorId, selectedProductStyleId: selectedProductStyleId }, cancellation, false);
  }

  checkIfProductIsOftypeRepairAsync(productStyleId: number, timeout: Promise<any>): Promise<boolean> {
    const data = { productStyleId: productStyleId };
    return this.httpClient.getJsonAsync<boolean>('ProductStyles/CheckIfProductIsOftypeRepair', data, timeout, false);
  }
  deleteProductStyleColor(Id: number, ProductstyleId: number, cancellation: Promise<any>): Promise<any> {
    const request: DeletProductStyleColor = { id: Id, productStyleId: ProductstyleId };
    return this.httpClient.postJsonAsync('ProductStyleColors/DeleteProductStyleColors', request, cancellation);
  }


  ValidateManufacturerColor(ManufacturerCodeId: number, sku: any, Id: number, ProductstyleId: number, timeout: Promise<any>): Promise<boolean> {
    const request = { ManufacturerCodeId: ManufacturerCodeId, sku: sku, Id: Id, ProductstyleId: ProductstyleId };
    return this.httpClient.getJsonAsync('ProductStyleColors/ValidateManufacturerColor', request, timeout);
  }

  copy(id: number, name: string, cancellationToken: Promise<any>): Promise<number> {
    const request: ProductCopyRequest = { id: id, name: name };
    return this.httpClient.postJsonAsync('Products/Copy', request, cancellationToken);
  }

  onAddProductsKitAction(productStyleIds: number[], productId: number, kittype: ProductKitTypeEnum, active: boolean, cancellation: Promise<any>): Promise<any> {
    const request: ProductKitViewModel = { productStyleIds: productStyleIds, productId: productId, kitType: kittype, active: active };
    return this.httpClient.postJsonAsync('ProductKits/AddMultipleKitItems', request, cancellation);
  }

  onAddProductsBundleAction(productStyleIds: number[], productId: number, bundletype:ProductBundleTypeEnum, active: boolean, cancellation: Promise<any>): Promise<any> {
    const request: ProductBundleViewModel = { productStyleIds: productStyleIds, productId: productId, bundleType: bundletype, active: active };
    return this.httpClient.postJsonAsync('ProductBundles/AddMultipleBundleItems', request, cancellation);
  }

  onAddProductsOptionAction(productStyleIds: number[], productId: number, active: boolean, cancellation: Promise<any>): Promise<any> {
    const request: ProductKitViewModel = { productStyleIds: productStyleIds, productId: productId, active: active };
    return this.httpClient.postJsonAsync('ProductOptions/AddMultipleOptionItems', request, cancellation);
  }

  getManufacturerColorOptionsIncludeInActiveAsync(productStyleId: number, includeInActive: boolean = false, cancellationToken: Promise<any> = null): Promise<ListOption<number>[]> {
    const request: ProductStyleColorsOptionRequest = { searchString: null, productStyleId: productStyleId, includeInActive: includeInActive };
    return this.httpClient.getJsonAsync<ListOption<number>[]>('ProductStyleColors/Options', request, cancellationToken);
  }

  getManufacturerColorOptionsAsync(productStyleId: number, cancellationToken: Promise<any> = null): Promise<ListOption<number>[]> {
    const request: ProductStyleColorsOptionRequest = { searchString: null, productStyleId: productStyleId };
    return this.httpClient.getJsonAsync<ListOption<number>[]>('ProductStyleColors/Options', request, cancellationToken);
  }

  checkIfOrderIntegrationAllowedForProduct(productStyleId: number, clinicId: number, timeout: Promise<any> = null): Promise<boolean> {
    const data = { productStyleId: productStyleId, clinicId: clinicId };
    return this.httpClient.getJsonAsync<boolean>('ProductStyles/CheckIfOrderIntegrationAllowedForProduct', data, timeout, false);
  }
}
